import { _decorator, Enum, CCFloat, Vec3, v3, CCInteger, Node, CurveRange } from 'cc';
import { DEV } from 'cc/env';
import BaseMovingEffect from '../../Components/MovingEffect/BaseMovingEffect';
import { EasingType } from '../../Utils/EasingType';
import BaseAction from './BaseAction';

const { ccclass, property } = _decorator;

// 類型
export enum MovingEffectType {
    Bezier,
    Tween,
}
// 行為
export enum MovingEffectActionType {
    Play,
    Stop,
}

@ccclass('MovingEffectAction')
export default class MovingEffectAction extends BaseAction {
    @property({ type: BaseMovingEffect, displayName: DEV && '飛行物件', tooltip: DEV && '要操作的飛行物件' })
    public readonly target: BaseMovingEffect = null;
    @property({ type: Enum(MovingEffectActionType), displayName: DEV && '操作類型', tooltip: DEV && '要進行什麼行為\r\nActive: 顯示\r\nInActive: 關閉' })
    public readonly actionType: MovingEffectActionType = MovingEffectActionType.Play;
    @property({ type: Enum(MovingEffectType), displayName: DEV && '路徑組件', tooltip: DEV && '選擇路徑實做的機制:\r\nBezier: 路徑編輯器' })
    public readonly type: MovingEffectType = MovingEffectType.Bezier;

    @property({
        type: Node,
        displayName: DEV && '起點',
        tooltip: DEV && '飛行起點',
        visible(this: MovingEffectAction) {
            return (this.type === MovingEffectType.Bezier && this.rescalePath) || this.type === MovingEffectType.Tween;
        },
    })
    public readonly from: Node = null;
    @property({
        type: Node,
        displayName: DEV && '終點',
        tooltip: DEV && '飛行終點',
        visible(this: MovingEffectAction) {
            return (this.type === MovingEffectType.Bezier && this.rescalePath) || this.type === MovingEffectType.Tween;
        },
    })
    public readonly to: Node = null;

    @property({
        displayName: DEV && '起點座標',
        tooltip: DEV && '飛行起點座標',
        visible(this: MovingEffectAction) {
            return !this.from && ((this.type === MovingEffectType.Bezier && this.rescalePath) || this.type === MovingEffectType.Tween);
        },
    })
    public readonly startPosition: Vec3 = v3();
    @property({
        displayName: DEV && '終點座標',
        tooltip: DEV && '飛行終點座標',
        visible(this: MovingEffectAction) {
            return !this.to && ((this.type === MovingEffectType.Bezier && this.rescalePath) || this.type === MovingEffectType.Tween);
        },
    })
    public readonly endPosition: Vec3 = v3();

    @property({
        displayName: DEV && '表演時長',
        tooltip: DEV && '額外指定表演時長, (單位: 豪秒)\r\n該數值為0時使用路徑預設時長',
        type: CCFloat,
    })
    public readonly duration: number = 0;

    // #region Bezier
    @property({
        displayName: DEV && '路徑名稱',
        tooltip: DEV && '從編輯器輸出的路徑名稱, \r\n要先用 BezierPathLoader 載入',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Bezier;
        },
    })
    public readonly pathName: string = '';
    @property({
        displayName: DEV && '速度倍率',
        tooltip: DEV && '額外指定速度倍率',
        type: CCFloat,
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Bezier;
        },
    })
    public readonly speedRate: number = 1;

    @property({
        displayName: DEV && '額外起點與終點',
        tooltip: DEV && '指定額外的起點與終點, 將整段路徑以這兩點為基準變形',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Bezier;
        },
    })
    public readonly rescalePath: boolean = false;
    @property({
        displayName: DEV && '反向路徑',
        tooltip: DEV && '從終點飛回起點',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Bezier;
        },
    })
    public readonly reverse: boolean = false;
    // #endregion

    // #region Tween
    @property({
        displayName: DEV && '開始時間',
        tooltip: DEV && '設定Tween要從第幾秒開始播放, \r\n單位: 豪秒',
        min: 0,
        type: CCInteger,
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Tween;
        },
    })
    public readonly startAt: number = 0;
    @property({
        displayName: DEV && '座標偏移',
        tooltip: DEV && '將整段飛行路徑做偏移',
        visible(this: MovingEffectAction) {
            return !this.to && this.type === MovingEffectType.Bezier;
        },
    })
    public readonly positionOffset: Vec3 = v3();
    @property({
        displayName: DEV && '覆寫固定角度',
        tooltip: DEV && '是否要覆蓋路徑原有的固定角度開關',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Bezier;
        },
    })
    public readonly overrideFixedRotate: boolean = false;
    @property({
        displayName: DEV && '固定角度',
        tooltip: DEV && '啟用時, 飛行物件會保持原有角度, 不依照飛行路徑調整角度',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Bezier && this.overrideFixedRotate;
        },
    })
    public readonly fixedRotate: boolean = false;
    @property({
        displayName: DEV && '覆寫原始速度',
        tooltip: DEV && '是否要覆蓋路徑原有的速度曲線設定',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Bezier;
        },
    })
    public readonly overrideSpeedCurve: boolean = false;
    @property({
        displayName: DEV && '使用Easing曲線',
        tooltip: DEV && '是否要使用內建的Easing曲線',
        visible(this: MovingEffectAction) {
            return this.overrideSpeedCurve;
        },
    })
    public readonly overrideSpeedCurveUseEasing: boolean = false;
    @property({
        type: Enum(EasingType),
        displayName: DEV && 'Easing速度曲線',
        tooltip: DEV && '覆寫的速度曲線設定',
        visible(this: MovingEffectAction) {
            return this.overrideSpeedCurve && this.overrideSpeedCurveUseEasing;
        },
    })
    public readonly easingSpeedCurve: EasingType = EasingType.linear;
    @property({
        displayName: DEV && '覆寫速度曲線',
        tooltip: DEV && '要使用哪條速度曲線',
        visible(this: MovingEffectAction) {
            return this.overrideSpeedCurve && !this.overrideSpeedCurveUseEasing;
        },
    })
    public readonly speedCurve: string = '';
    @property({
        displayName: DEV && '開始時調整縮放',
        tooltip: DEV && '是否在開始時調整縮放倍率',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Tween;
        },
    })
    public readonly overrideOriginScale: boolean = false;
    @property({
        displayName: DEV && '指定起始縮放',
        tooltip: DEV && 'TweenMovingEffect指定起始縮放倍率',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Tween && this.overrideOriginScale;
        },
    })
    public readonly originScale: Vec3 = new Vec3(1, 1, 1);
    @property({
        displayName: DEV && '指定目標縮放',
        tooltip: DEV && 'TweenMovingEffect指定目標縮放倍率',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Tween;
        },
    })
    public readonly targetScale: Vec3 = new Vec3(1, 1, 1);
    @property({
        displayName: DEV && '是否只更新X座標',
        visible(this: MovingEffectAction) {
            return !this.onlyUpdateY && this.type === MovingEffectType.Tween;
        },
    })
    public readonly onlyUpdateX: boolean = false;
    @property({
        displayName: DEV && '是否只更新Y座標',
        visible(this: MovingEffectAction) {
            return !this.onlyUpdateX && this.type === MovingEffectType.Tween;
        },
    })
    public readonly onlyUpdateY: boolean = false;
    @property({
        displayName: DEV && '覆寫表演曲線',
        tooltip: DEV && '是否要覆蓋路徑原有的表演曲線設定',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Tween;
        },
    })
    public readonly overrideTweenCurve: boolean = false;
    @property({
        type: CurveRange,
        displayName: DEV && 'x 表演曲線',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Tween && this.overrideTweenCurve;
        },
    })
    public readonly xAxisCurve: CurveRange = new CurveRange();
    @property({
        type: CurveRange,
        displayName: DEV && 'y 表演曲線',
        visible(this: MovingEffectAction) {
            return this.type === MovingEffectType.Tween && this.overrideTweenCurve;
        },
    })
    public readonly yAxisCurve: CurveRange = new CurveRange();
    // #endregion
}
